import { TranslationKey } from './../../../types/translation/TranslationKey';

import React, { useContext, useMemo, useState } from 'react';
import { AlertContext } from '../../../context/AlertContext';
import { Alert } from '../../../types/alert';
import { Grid } from '@mui/material';
import { testIds } from '../../../util/identifiers/identifiers.util';
import { TriggerEntityType } from '../../../types/trigger';
import { StockLocationAggregateType } from '../../../types/common/stockLocationAggregateType';
import { t } from '../../../types/translation/Translator';
import { useMutation } from '@apollo/client';
import { AlertMutations, ProcessAlertResponse, ProcessAlertVariables } from '../../../graphql/alert.graphql';
import { StockLocationRoleAssignmentContext } from '../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import Table, { TableHeader } from '../../components/Table';
import Pane from '../../../VentoryUI/components/common/Pane';
import FlexPane from '../../../VentoryUI/components/common/FlexPane';
import SearchBarWithFilter from '../../../VentoryUI/components/common/SearchBarWithFilter';
import { AlertFilter } from '../../../VentoryUI/components/filters/AlertFilter/AlertFilter';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { BinStatusContext } from '../../../context/BinStatusContext';
import { TagRelationContext } from '../../../context/TagRelationContext';
import { LotContext } from '../../../context/LotContext';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import Provider from '../../../providers/common/Provider';
import { DateTimeUtil } from '../../../util/date.util';
import dayjs from 'dayjs';
import { UserContext } from '../../../context/UserContext';
import { CompanyContext } from '../../../context/CompanyContext';

export default function AlertScreen() {
  return (
    <AlertScreenProvider>
      <Element />
    </AlertScreenProvider>
  );
}

function Element() {
  const { alerts, alertsLoading, setAlerts } = useContext(AlertContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { lots } = useContext(LotContext);
  const { customFields } = useContext(CustomFieldContext);
  const { locale } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);

  const allItems = useMemo(() => {
    return [...alerts.values()].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [alerts]);

  const [items, setItems] = useState([...alerts.values()]);
  const [error, setError] = useState<string>('');

  const [processAlert, { loading }] = useMutation<ProcessAlertResponse, ProcessAlertVariables>(AlertMutations.process, {
    onCompleted: response => {
      alerts.set(response.processAlert.id, new Alert(response.processAlert));
      setAlerts(new Map(alerts));
    },
    onError: error => {
      setError(error.message);
    },
  });

  async function handleProcess(alert: Alert) {
    await processAlert({ variables: { alertId: alert.id, companyId: alert.companyId } });
  }

  const headers: TableHeader<Alert>[] = [
    {
      key: 'alertTime',
      name: t(TranslationKey.dateAndTime).singular.label,
      text: (item: Alert) => DateTimeUtil.toLocaleDateTimeString(locale, dayjs(item.createdAt).toDate()),
      sortValue: (item: Alert) => dayjs(item.createdAt),
    },
    {
      key: 'alert',
      name: t(TranslationKey.alert).plural.label,
      text: (item: Alert) => item.alertString ?? '',
      weight: 4,
    },
    {
      key: 'process',
      name: '',
      text: (item: Alert) => {
        if (
          !item.processed &&
          item.entityType === TriggerEntityType.pmd &&
          item.entityValue.includes('${REORDER_RULE}') &&
          hasStockLocationRole(item.companyId, item.stockLocationIds[0], StockLocationRole.STOCK_LOCATION_MANAGER) &&
          (item.stockLocationAggregateType === StockLocationAggregateType.any ||
            item.stockLocationAggregateType === StockLocationAggregateType.anyOf)
        ) {
          return (
            <Grid item xs={12} display={'flex'} justifyContent={'end'}>
              <p className='h-7 py-1 text-sm font-semibold text-blue-500' onClick={() => handleProcess(item)}>
                {t(TranslationKey.createOrder).singular.label}
              </p>
            </Grid>
          );
        }

        return '';
      },
    },
  ];

  const filter = new AlertFilter(productMasterData, binStatuses, tagRelations, lots, customFields, currentCompany);

  return (
    <Pane testId={testIds.alertScreen} error={error}>
      <FlexPane
        header={
          <SearchBarWithFilter
            loading={productMasterDataLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.filterAlerts).singular.label}
            filter={filter}
          />
        }
        content={
          <Table
            title={t(TranslationKey.alert).plural.label}
            loading={alertsLoading}
            items={items}
            headers={headers}
            totalItemCount={allItems.length}
            filter={filter}
          />
        }
      />
    </Pane>
  );
}

function AlertScreenProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider alert stockLocationRoleAssignment productMasterData binStatus tagRelation lot>
      {children}
    </Provider>
  );
}
